import { useNavigate, useParams } from "react-router-dom";
import { disable, idcard, trashOutline } from "../../../asset";
import PageHeader from "../../../components/PageHeader";
import Tabs from "../../../components/Tabs";

import {
  EditModeProvider,
  useEditMode,
} from "../../../context/EditModeContext";
import { Student } from "../../../types/studentType";
import { JSX, useEffect, useState } from "react";
import ProfileInformation from "./ProfileInformation";
import OtherDetails from "./OtherDetails";
import MedicalDetalis from "./MedicalDetails";
import EscortDetalis from "./EscortDetails";
import FeesList from "../fees/FeesList";
import AdmissionDetails from "../admission-details/AdmissionDetails";
import StudentApis from "../../../api/studentApis";
import useConfirmToast from "../../../util/hooks/useConfirmToast";
import { toast } from "sonner";
import useUrlTab from "../../../util/hooks/useUrlTab";
import Attendance from "../attendance-tab/Attendance";
import Others from "../others-tab/Others";
import SubButton from "../../../components/SubButtons";
import { CustomButton } from "../../../components";
import { useFormEdit } from "../../../util/hooks/useFormEdit";
import { IS_LIVE } from "../../../util/config/env";

const tabOptions = [
  "Student Details",
  "Attendance",
  "Admission Details",
  ...(IS_LIVE ? [] : ["Fee"]),
  "Others",
];

function StudentProfileContent({ studentData }: { studentData?: Student }) {
  const { activeTab, handleTabChange } = useUrlTab(tabOptions);
  const { isEditing, toggleEdit } = useEditMode();
  const navigate = useNavigate();
  const { confirm, modal } = useConfirmToast();
  const form = useFormEdit<Student>(studentData);

  const handleUpdate = async () => {
    try {
      await StudentApis.updateStudent(form.formData);
      toggleEdit(); // exit edit mode
    } catch {
      toast.error("Failed to update student");
    }
  };

  const tabContent: Record<string, JSX.Element> = {
    "Student Details": (
      <>
        <ProfileInformation form={form} />
        <OtherDetails form={form} />
        <EscortDetalis studentId={studentData?.admissionNumber} />
        <MedicalDetalis form={form} />
        <div className="flex justify-end items-end relative group px-10">
          <CustomButton
            color="bg-primary-500"
            text="Submit"
            onClick={handleUpdate}
            disabled={!isEditing}
          />
        </div>
      </>
    ),
    Attendance: <Attendance />,
    "Admission Details": (
      <div>
        <AdmissionDetails studentData={studentData} />
      </div>
    ),
    Fee: (
      <div>
        <FeesList admissionNumber={studentData?.admissionNumber} />
      </div>
    ),
    Others: (
      <Others
        admissionNumber={studentData?.admissionNumber}
        groupId={studentData?.groupId}
        groupName={studentData?.groupName}
      />
    ),
  };

  const handleDelete = async () => {
    // if (!studentData?.admissionNumber) return;

    const confirmed = await confirm(
      "Are you sure you want to delete this Student?",
    );

    if (!confirmed) return;

    try {
      await StudentApis.deleteStudent(Number(studentData.admissionNumber));
      navigate("/all-students");
    } catch {
      toast.error("Failed to delete Student");
    }
  };

  return (
    <div className="flex flex-col gap-5 overflow-hidden">
      <div className="flex flex-col md:flex-row gap-4 justify-between md:items-center">
        <PageHeader showBack onBack={() => navigate("/all-students")} />
        <div className="flex flex-wrap justify-end items-end gap-2">
          {!IS_LIVE && (
            <>
              <SubButton
                text="Disable"
                color="bg-background-800"
                icon={disable}
              />
              <SubButton
                text="ID Card"
                color="bg-success-600"
                icon={idcard}
                onClick={() =>
                  navigate(
                    `/all-students/student-profile/id-card/${studentData?.admissionNumber}`,
                  )
                }
              />
            </>
          )}
          <SubButton
            text="Delete"
            color="bg-error-600"
            onClick={handleDelete}
            icon={trashOutline}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg w-full grid  py-5 divide-y divide-neutral-50">
        <div className="w-full flex flex-col lg:flex-row justify-between gap-4 lg:items-center overflow-x-auto lg:overflow-visible">
          <div className="lg:flex-1 flex justify-center items-center">
            <Tabs
              tabs={tabOptions}
              onTabChange={handleTabChange}
              activeTab={activeTab}
            />
          </div>
          {(activeTab === "Student Details" ||
            activeTab === "Admission Details") && (
            <div className="flex justify-end items-end px-7 pb-4 lg:pb-0">
              <button
                onClick={toggleEdit}
                className={`${isEditing ? "bg-error-500" : "bg-warning-600"} py-1 px-5 rounded font-semibold text-[13px] text-white`}
              >
                {isEditing ? "Cancel" : "Edit"}
              </button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">{tabContent[activeTab]}</div>
      </div>
      {modal}
    </div>
  );
}

export default function ViewStudentProfile() {
  const { id } = useParams();
  const [studentData, setStudentData] = useState<Student | null>(null);

  useEffect(() => {
    if (!id) return;

    StudentApis.getStudentById(Number(id)).then((res) => {
      if (res?.status === 1) {
        setStudentData(res.data);
      }
    });
  }, [id]);

  return (
    <EditModeProvider>
      <StudentProfileContent studentData={studentData ?? undefined} />
    </EditModeProvider>
  );
}
